import { Box, Button, Divider, Flex, FormControl, FormLabel, Input, Modal, ModalBody, ModalContent, ModalHeader, ModalOverlay, Text, useDisclosure, } from "@chakra-ui/react";
import MyDiv from "./newJobOpeningCard.style";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { MultiSelectMenu } from "../../../../../components/MultiSelectMenu/MultiSelectMenu";
import { AlertCard, CustomButton, CustomToast } from "../../../../../components";
import Lottie from "lottie-react";
import SuccessAnimation from "../../../../../assets/lottie/Success.json";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { useFetch } from "../../../../../hooks/useFetch";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { IJobOpening, emptyJobOpening, schema, JobOpeningCardProps, urgencyLevelOptions } from "./modal";
import { Dropdown } from "../../../../../components/DropDown/DropDown";
import { HrlenseInput } from "../../../../../components/Input/Input";
import { PayrollOption } from "../../../../Employees/AddNewEmployee/components/Employment&Experience/model";


const NewJobOpeningCard = forwardRef<{ attemptClose: () => void }, JobOpeningCardProps>(({ jobData, handleDrawer, loadJobOpening }, ref) => {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const [selectedSkills, setSelectedSkills] = useState<any[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [alertOpen, setAlertOpen] = useState(false);
  const [payrollCompanyOptions, setPayrollCompanyOptions] = useState<PayrollOption[]>([]);
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [jobProfileOptions, setJobProfileOptions] = useState<any[]>([]);
  const [employementType, setEmploymentType] = useState<any[]>([]);
  const [jobSummary, setJobSummary] = useState<string>("");
  const [workMode, setWorkMode] = useState<any[]>([]);
  const [jobSkills, setJobSkills] = useState<any[]>([]);
  const [jobPostingLoading, setJobPostingLoading] = useState(false);
  const [bussinessProcess, setBussinessProcess] = useState<any[]>([]);
  const [generatingJD, setGeneratingJD] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);

  const { handleSubmit, control, formState: { errors, isDirty }, reset, watch, setValue } = useForm<IJobOpening>({
    resolver: yupResolver(schema),
    defaultValues: emptyJobOpening,
    mode: "onBlur",
  });

  const formatDate = (date?: string | null) => {
    if (!date) return "";

    if (date.includes("T")) {
      return date.split("T")[0];
    }

    return date.replace(/\//g, "-");
  };


  useEffect(() => {
    if (jobData) {
      const formattedData = {
        ...jobData,
        job_Opening_Date: formatDate(jobData.job_Opening_Date),
        job_Closing_Date: formatDate(jobData.job_Closing_Date),
        expected_Date_Of_Joining: formatDate(jobData.expected_Date_Of_Joining),
      };

      reset(formattedData);

      if (jobData.skill) {
        const skillsArray = jobData.skill.split(",").map((s: string) => parseInt(s, 10));
        setSelectedSkills(skillsArray);
      } else {
        setSelectedSkills([]);
      }

      setJobSummary(jobData.jd_Description || "");

      if (jobData.payroll_Company_Id) {
        loadBranchOptions(jobData.payroll_Company_Id);
      }
      if (jobData.department_Id) {
        loadJobProfile(jobData.department_Id);
      }
      if (jobData.department_Id && jobData.job_Profile) {
        loadJobSkills(jobData.department_Id, jobData.job_Profile);
      }
    }
  }, [jobData]);

  const handleConfirmStatusChange = () => {
    setAlertOpen(false);

    handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: IJobOpening) => {
    try {
      const payload = {
        ...data,
        skills: selectedSkills.map((s: any) => typeof s === "object" ? s.id : s),
        jd_Description: jobSummary,
      };
      setJobPostingLoading(true);

      let res;
      if (jobData?.id) {
        res = await fetchApi("JobOpening/JobPosting", "PUT", payload, "", "Job Opening Updated Successfully");
      } else {
        res = await fetchApi("JobOpening/JobPosting", "POST", payload, "", "Job Opening Posted Successfully");
      }

      if (res) {
        setAlertOpen(false);
        onOpen();
        setTimeout(() => { onClose(); }, 1500);
        setTimeout(() => { handleDrawer(false); }, 1600);
        reset();
        setSelectedSkills([]);
        setJobSummary("");
        loadJobOpening();
        setJobPostingLoading(false);
      }
      setJobPostingLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const ClearDrawer = () => {
    reset(emptyJobOpening);
    setSelectedSkills([]);
    setJobSummary("");
  };

  const PayrollCompany = async () => {
    const response = await fetchApi("DropDown/payrollCompany", "GET", null, "", "");
    if (response) setPayrollCompanyOptions(response);
  };
  const loadBranchOptions = async (payrollCompanyId: number) => {
    const res = await fetchApi("DropDown/payRollBranches", "GET", null, `payroll_Id=${payrollCompanyId}`, "");
    if (res) setBranchOptions(res);
  };

  const loadDepartmentOptions = async () => {
    const res = await fetchApi("DropDown/department", "GET", null, "", "");
    if (res) setDepartmentOptions(res);
  };

  const loadJobProfile = async (department_Id?: number) => {
    const res = await fetchApi("DropDown/getJobProfile", "GET", null, `department_Id=${department_Id}`, "");
    if (res) setJobProfileOptions(res);
  };

  const LoadEmploymentType = async () => {
    const res = await fetchApi("RecruitmentMaster/employmentType", "GET", null, "");
    if (res) setEmploymentType(res);
  };

  const LoadWorkMode = async () => {
    const res = await fetchApi("RecruitmentMaster/WorkMood", "GET", null, "");
    if (res) setWorkMode(res);
  };

  const loadJobSkills = async (department_Id?: number, job_Profile?: number) => {
    const query = `department_Id=${department_Id}&job_Profile=${job_Profile}`
    const res = await fetchApi("RecruitmentDropDown/JobSkills", "GET", null, query, "");
    if (res) setJobSkills(res);
  }


  const loadBussinessProcess = async () => {
    const res = await fetchApi("DropDown/businessProcess", "GET", null, "", "");
    if (res) setBussinessProcess(res);
  };

  useEffect(() => {
    PayrollCompany();
    loadDepartmentOptions();
    LoadEmploymentType();
    LoadWorkMode();
    loadBussinessProcess();
  }, []);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === "payroll_Company_Id") {
        if (value.payroll_Company_Id) {
          loadBranchOptions(value.payroll_Company_Id)
        } else {
          loadBranchOptions(0)
        }
      }
      if (name === "department_Id") {
        if (value.department_Id) {
          loadJobProfile(Number(value.department_Id))
        } else {
          loadJobProfile(0)
        }
        setSelectedSkills([]);
        setJobSkills([]);
      }
      if (name === "job_Profile") {
        setSelectedSkills([]);
        setJobSkills([]);
      }
      if ((name === "department_Id" || name === "job_Profile") && value.department_Id && value.job_Profile) {
        loadJobSkills(Number(value.department_Id), Number(value.job_Profile));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);


  const cleanGeneratedHtml = (html: string) => {
    return html
      .replace(/<h[1-4][^>]*>\s*(basic information|additional (details|information))\s*<\/h[1-4]>/gi, "")
      .trim();
  };

  const handleGenerateJD = async () => {
    const values = watch();

    if (!values.job_Profile || !values.business_Process_Id || !values.department_Id) {
      addToast({ message: "Please fill Business Process, Department and Job Title before generating JD.", status: "warning" });
      return;
    }

    const payload = {
      job_Profile_Id: Number(values.job_Profile),
      business_Process_Id: Number(values.business_Process_Id),
      department_Id: Number(values.department_Id),
      employement_Type_Id: Number(values.employement_Type_Id),
      experience_From: values.experience_From,
      experience_To: values.experience_To,
      skills: selectedSkills.map((s: any) => (typeof s === "object" ? s.skill : s)),
      work_Mood: values.work_Mood,
      number_Of_Openings: values.number_Of_Openings,
      salary_Range_Min: values.salaryRangeMin,
      salary_Range_Max: values.salaryRangeMax,
      expected_Date_Of_Joining: values.expected_Date_Of_Joining,
      urgency_Level: values.urgency_Level,
    };

    setGeneratingJD(true);
    const res = await fetchApi("JobOpening/generate-jd", "POST", payload, "", "");
    setGeneratingJD(false);

    if (res?.jobDescription) {
      const cleaned = cleanGeneratedHtml(res.jobDescription);
      setJobSummary(cleaned);
      setValue("jobSummary", cleaned, { shouldValidate: true, shouldDirty: true });
      setEditorKey(prev => prev + 1);
    };
  }


  const hasUnsavedChanges = isDirty || selectedSkills.length > 0 || jobSummary.trim() !== "";

  const attemptClose = () => {
    if (hasUnsavedChanges) {
      setCloseConfirmOpen(true);
    } else {
      handleDrawer(false);
    }
  };

  const handleDiscard = () => {
    setCloseConfirmOpen(false);
    reset(emptyJobOpening);
    setSelectedSkills([]);
    setJobSummary("");
    handleDrawer(false);
  };
  const handleSaveDraft = async () => {
    const values = watch();

    const payload = {
      id: jobData?.isDraft ? jobData.id : undefined,
      ...Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value === "" ? null : value,])),
      skills: selectedSkills.map((s: any) => typeof s === "object" ? s.id : s),
    };

    try {
      setDraftSaving(true);

      const res = await fetchApi("JobOpening/SaveDraft", "POST", payload, "", "Saved as draft");

      if (res) {
        setCloseConfirmOpen(false);
        reset(emptyJobOpening);
        setSelectedSkills([]);
        setJobSummary("");
        loadJobOpening();
        handleDrawer(false);
      }
    } finally {
      setDraftSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ attemptClose }));

  return (
    <MyDiv>
      <Box py={2} as="form" onSubmit={handleSubmit(onSubmit)}>
        <Text className="font-poppins text_medium text_lg">Basic Information</Text>
        <Flex gap={8} mt={2}>
          <Dropdown isRequired name="business_Process_Id" label="Business Process" control={control} errors={errors} options={bussinessProcess} valueKey="business_Process_Id" labelKey="business_Process_Name" />
          <Dropdown isRequired name="payroll_Company_Id" label="Payroll Company" control={control} errors={errors} options={payrollCompanyOptions} valueKey="id" labelKey="payroll_Company_Name" />
          <Dropdown isRequired name="branch_Id" label="Branch" control={control} errors={errors} options={branchOptions} valueKey="branch_ID" labelKey="branchName" />
        </Flex>
        <Flex gap={8} mt={2}>
          <Dropdown isRequired name="department_Id" label="Department" control={control} errors={errors} options={departmentOptions} valueKey="id" labelKey="departmentName" />
          <Dropdown isRequired name="job_Profile" label="Job Title" control={control} errors={errors} options={jobProfileOptions} valueKey="id" labelKey="job_Profile" />
          <HrlenseInput isRequired name="number_Of_Openings" control={control} errors={errors} label="Number of openings" type="number" placeholder="Enter Number of Openings" />
        </Flex>

        <Flex gap={8} mt={2}>
          <Dropdown isRequired name="employement_Type_Id" label="Employement Type" control={control} errors={errors} options={employementType} valueKey="id" labelKey="selection_Term" />
          <HrlenseInput isRequired name="job_Opening_Date" control={control} errors={errors} label="Job Opening Date" type="date" placeholder="Select Job Opening Date" min={new Date().toISOString().split("T")[0]} />
          <HrlenseInput isRequired name="job_Closing_Date" control={control} errors={errors} label="Job Closing Date" type="date" placeholder="Select Job Closing Date" min={new Date().toISOString().split("T")[0]} />
        </Flex>

        <Flex gap={8} mt={2}>
          <HrlenseInput isRequired name="salaryRangeMin" control={control} errors={errors} label="Salary Range(min)" type="number" placeholder="Enter Salary Min" />
          <HrlenseInput isRequired name="salaryRangeMax" control={control} errors={errors} label="Salary Range(max)" type="number" placeholder="Enter Salary Max" />
          <Dropdown isRequired name="urgency_Level" label="Urgency Level" control={control} errors={errors} options={urgencyLevelOptions} valueKey="id" labelKey="label" />
        </Flex>

        <Flex gap={8} mt={2}>
          <Dropdown isRequired name="work_Mood" label="Work Mode" control={control} errors={errors} options={workMode} valueKey="id" labelKey="work_Mood" />
          <HrlenseInput isRequired name="expected_Date_Of_Joining" control={control} errors={errors} label="Expected DOJ" type="date" placeholder="Select Expected Date of Joining" min={new Date().toISOString().split("T")[0]} />
          <FormControl isRequired>
            <MultiSelectMenu
              label="Required Skills"
              options={jobSkills}
              selectedValues={selectedSkills}
              setSelectedValues={setSelectedSkills}
              labelKey="skill"
              valueKey="id"
              placeholder="Select Skills"
            />
          </FormControl>
        </Flex>

        {/* <Flex gap={8} mt={2}>
        </Flex>

        <Flex gap={8} mt={2}>

        </Flex> */}

        <Text className="font-poppins text_medium text_lg"> Additional Details</Text>
        <Flex gap={8} mt={2} mb={3}>
          {/* <HrlenseInput isRequired name="experience_From" control={control} errors={errors} label="Experience (in years) Min" type="number" placeholder="0 year" />
          <HrlenseInput isRequired name="experience_To" control={control} errors={errors} label="Experience (in years) Max" type="number" placeholder="2 year" /> */}
          <Controller
            name="experience_From"
            control={control}
            render={({ field }) => (
              <FormControl isRequired isInvalid={!!errors.experience_From}>
                <FormLabel>Experience (in Months) Min</FormLabel>
                <Input
                  {...field}
                  type="number"
                  placeholder="0 month"
                  value={field.value ?? ""}
                  onChange={(e) => {
                    let value = e.target.value.replace(/^0+(?=\d)/, "");
                    let cappedValue = Math.min(Number(value || 0), 50);
                    field.onChange(value === "" ? "" : cappedValue);
                  }}
                />
                {errors.experience_From && (
                  <Text color="red.500">{errors.experience_From.message}</Text>
                )}
              </FormControl>
            )}
          />

          <Controller
            name="experience_To"
            control={control}
            render={({ field }) => (
              <FormControl isRequired isInvalid={!!errors.experience_To}>
                <FormLabel>Experience (in months) Max</FormLabel>
                <Input
                  {...field}
                  type="number"
                  placeholder="0 months"
                  value={field.value ?? ""}
                  onChange={(e) => {
                    let value = e.target.value.replace(/^0+(?=\d)/, "");
                    let cappedValue = Math.min(Number(value || 0), 50);
                    field.onChange(value === "" ? "" : cappedValue);
                  }}
                />
                {errors.experience_To && (
                  <Text color="red.500">{errors.experience_To.message}</Text>
                )}
              </FormControl>
            )}
          />
        </Flex>

        <Flex justify="space-between" align="center">
          <Text className="font-poppins text_medium text_lg">Job Description</Text>
          <CustomButton
            title="Generate JD with AI"
            onClick={handleGenerateJD}
            isLoading={generatingJD}
            className="abc font-poppins text_sm text_medium"
          />
        </Flex>
        <FormControl mb={3} isRequired mt={2}>
          <FormLabel className="font-poppins text_regular text_md">Job Summary</FormLabel>
          <Controller
            name="jobSummary"
            control={control}
            render={({ field }) => (
              <SunEditor
                key={editorKey}
                height="200px"
                placeholder="Enter Job Summary"
                setOptions={{
                  buttonList: [
                    ["undo", "redo"],
                    ["bold", "italic", "underline", "strike"],
                    ["list", "align", "fontSize", "formatBlock"],
                    ["link", "image", "video"],
                    ["removeFormat"]
                  ]
                }}
                setContents={field.value || ""}
                onChange={field.onChange}
              />
            )}
          />
          {errors.jobSummary && (
            <Text color="red.500" fontSize="sm">{errors.jobSummary.message}</Text>
          )}
        </FormControl>

        <Box>
          <Flex gap={3} justify="flex-end">
            <CustomButton title="Clear" onClick={ClearDrawer} className=" abc font-poppins text_sm text_medium" />
            <CustomButton title={jobData ? "Update Job" : "Publish Job"} className="btn abc font-poppins text_sm text_medium" onClick={() => setAlertOpen(true)} isLoading={jobPostingLoading} />
          </Flex>
        </Box>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent borderRadius="lg" p={2}>
          <ModalHeader textAlign="center">{jobData ? "Update Job" : "Publish Job"}</ModalHeader>
          <Divider />
          <ModalBody>
            <Box textAlign="center" p={1}>
              <Box display="flex" justifyContent="center" alignItems="center">
                <Lottie animationData={SuccessAnimation} loop={false} style={{ height: 120 }} />
              </Box>
              <Text mt={2} className="font-poppins text_sm text_semibold" color="green.600" > {jobData ? "Update Successful" : "Publish Successful"}</Text>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertCard
        isOpen={alertOpen}
        onClose={() => setAlertOpen(false)}
        onConfirm={handleConfirmStatusChange}
        title={jobData ? "Update Job" : "Publish Job"}
        description={jobData ? "Are you sure you want to update this Job?" : "Are you sure you want to publish this Job?"}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
      />


      <Modal isOpen={closeConfirmOpen} onClose={() => setCloseConfirmOpen(false)} isCentered size="sm">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent borderRadius="16px" p={0} overflow="hidden">
          <ModalBody p={6}>
            <Flex justify="center" mb={4}>
              <Flex align="center" justify="center" w="56px" h="56px" borderRadius="full" bg="#FFFBEB">
                <Text fontSize="26px" lineHeight="1">📝</Text>
              </Flex>
            </Flex>
            <Text textAlign="center" fontSize="16px" fontWeight="700" color="#0F172A" mb={1.5}>You have unsaved changes</Text>
            <Text textAlign="center" fontSize="13px" color="#64748B" mb={6} px={2}>Save this job opening as a draft to pick up later, or discard everything you've entered.</Text>

            <Flex direction="column" gap={2.5}>
              <Button bg="#006838" color="white" borderRadius="8px" fontSize="13px" fontWeight="600" h="42px" _hover={{ bg: "#005530" }} onClick={handleSaveDraft} isLoading={draftSaving} loadingText="Saving Draft...">Save as Draft</Button>
              <Button variant="outline" borderColor="#FCA5A5" color="#DC2626" borderRadius="8px" fontSize="13px" fontWeight="600" h="42px" bg="white" _hover={{ bg: "#FEF2F2", borderColor: "#F87171" }} onClick={handleDiscard} isDisabled={draftSaving}>Discard Changes</Button>
              <Button variant="ghost" color="#64748B" fontSize="13px" fontWeight="600" h="38px" _hover={{ bg: "#F1F5F9" }} onClick={() => setCloseConfirmOpen(false)} isDisabled={draftSaving}>Keep Editing</Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

    </MyDiv>
  );
});

export default NewJobOpeningCard;